import React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import {
    Tabs,
    Tab,
    Box,
    Typography,
    useMediaQuery,
    Paper,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";       // Objectives
import FunctionsIcon from "@mui/icons-material/Functions"; // Functions
import CodeIcon from "@mui/icons-material/Code";           // Coding
import JavascriptFunctionTable from "./JavascriptFunctionTable";
import JavascriptTutorials from "./JavascriptTutorials";
import JavaScriptCoding from "./JavaScriptCoding";

function JavaScriptFunctions({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

JavaScriptFunctions.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `tab-${index}`,
        "aria-controls": `tabpanel-${index}`,
    };
}

function DashBoard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [value, setValue] = React.useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <Paper
            sx={{
                minHeight: "100vh",
                backgroundColor: "#f8f8f8",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontFamily: "'Roboto', sans-serif",
            }}
        >
            {/* Tabs Section */}
            <Box
                sx={{
                    width: "100%",
                    mt: 16,
                    px: 3, // fixed 25px padding
                }}
            >
                <Tabs
                    orientation="horizontal"
                    variant="scrollable"
                    scrollButtons="auto"
                    value={value}
                    onChange={handleChange}
                    aria-label="JavaScript Function Tabs"
                    sx={{
                        "& .MuiTabs-flexContainer": {
                            justifyContent: isMobile ? "flex-start" : "center",
                            gap: 1,
                        },
                        "& .MuiTab-root": {
                            minWidth: isMobile ? "auto" : 220,
                            px: 2,
                            py: 1,
                            borderRadius: 20,
                            fontWeight: 600,
                            textTransform: "none",
                            color: "#100720",
                            bgcolor: "#fff",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                            transition: "all 0.3s",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            "&:hover": {
                                bgcolor: "#f7e600",
                            },
                        },
                        "& .Mui-selected": {
                            bgcolor: "goldenrod",
                            color: "#fff",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        },
                    }}
                >
                    <Tab
                        icon={<SchoolIcon />}
                        iconPosition="start"
                        label="Learning Objectives"
                        {...a11yProps(0)}
                    />
                    <Tab
                        icon={<FunctionsIcon />}
                        iconPosition="start"
                        label="In-built Functions"
                        {...a11yProps(1)}
                    />
                    <Tab
                        icon={<CodeIcon />}
                        iconPosition="start"
                        label="Application"
                        {...a11yProps(2)}
                    />
                </Tabs>
            </Box>

            {/* Content Section */}
            <Box
                sx={{
                    flexGrow: 1,
                    width: "100%",
                    mt: 4,
                    px: 3, // fixed 25px padding
                }}
            >
                <JavaScriptFunctions value={value} index={0} dir={theme.direction}>
                    <JavascriptTutorials />
                </JavaScriptFunctions>

                <JavaScriptFunctions value={value} index={1} dir={theme.direction}>
                    <JavascriptFunctionTable />
                </JavaScriptFunctions>

                <JavaScriptFunctions value={value} index={2} dir={theme.direction}>
                    <JavaScriptCoding />
                </JavaScriptFunctions>
            </Box>
        </Paper>
    );
}

export default DashBoard;
